import { useEffect, useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import { Check, X, ChevronDown, ChevronUp, Loader2, Mail } from 'lucide-react';

interface Application {
  id: string;
  business_name: string;
  website: string;
  full_name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

export default function AdminApplications() {
  const [apps, setApps] = useState<Application[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    loadApps();
  }, [filter]);

  const loadApps = async () => {
    setLoading(true);
    let query = supabase
      .from('merchant_applications')
      .select('*')
      .order('created_at', { ascending: false });
    if (filter !== 'all') query = query.eq('status', filter);
    const { data } = await query;
    setApps((data as Application[]) || []);
    setLoading(false);
  };

  const [approveError, setApproveError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  const updateStatus = async (id: string, status: 'approved' | 'rejected') => {
    setUpdatingId(id);
    setApproveError(null);

    if (status === 'approved') {
      try {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || SUPABASE_ANON_KEY;
        const siteOrigin = window.location.origin;
        const res = await fetch(`${SUPABASE_URL}/functions/v1/approve-merchant`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            application_id: id,
            return_url: `${siteOrigin}/register`,
            refresh_url: `${siteOrigin}/apply`,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to approve');
        }
        setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'approved' } : a)));
      } catch (err) {
        setApproveError(err instanceof Error ? err.message : 'Approval failed');
      }
    } else {
      await supabase
        .from('merchant_applications')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    }

    setUpdatingId(null);
  };

  const resendOnboardingEmail = async (id: string) => {
    setResendingId(id);
    setApproveError(null);
    setResendSuccess(null);
    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || SUPABASE_ANON_KEY;
      const siteOrigin = window.location.origin;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/resend-onboarding-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          application_id: id,
          return_url: `${siteOrigin}/register`,
          refresh_url: `${siteOrigin}/apply`,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send email');
      }
      setResendSuccess('Onboarding email sent successfully');
      setTimeout(() => setResendSuccess(null), 4000);
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to resend email');
    }
    setResendingId(null);
  };

  const statusColor = (s: string) => {
    if (s === 'pending') return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    if (s === 'approved') return 'text-green-400 bg-green-400/10 border-green-400/20';
    return 'text-red-400 bg-red-400/10 border-red-400/20';
  };

  const filters: { label: string; value: FilterStatus }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Applications</h1>
        <div className="flex items-center gap-1 rounded p-1" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded transition-colors"
              style={{
                color: filter === f.value ? 'var(--dash-text-primary)' : 'var(--dash-text-dim)',
                background: filter === f.value ? 'var(--dash-active-bg)' : undefined,
              }}
              onMouseEnter={(e) => {
                if (filter !== f.value) e.currentTarget.style.color = 'var(--dash-text-secondary)';
              }}
              onMouseLeave={(e) => {
                if (filter !== f.value) e.currentTarget.style.color = 'var(--dash-text-dim)';
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {resendSuccess && (
        <div className="mb-4 border border-green-500/30 bg-green-500/10 rounded px-4 py-3 flex items-center justify-between">
          <p className="font-sans text-sm text-green-300">{resendSuccess}</p>
          <button onClick={() => setResendSuccess(null)} className="text-green-400 hover:text-green-200 transition-colors ml-4">
            <X size={14} />
          </button>
        </div>
      )}

      {approveError && (
        <div className="mb-4 border border-red-500/30 bg-red-500/10 rounded px-4 py-3 flex items-center justify-between">
          <p className="font-sans text-sm text-red-300">{approveError}</p>
          <button onClick={() => setApproveError(null)} className="text-red-400 hover:text-red-200 transition-colors ml-4">
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
        </div>
      ) : apps.length === 0 ? (
        <div className="rounded p-12 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No applications found.</p>
        </div>
      ) : (
        <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
          <table className="w-full text-sm font-sans">
            <thead>
              <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>
                  Business
                </th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium hidden md:table-cell" style={{ color: 'var(--dash-text-dim)' }}>
                  Contact
                </th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>
                  Status
                </th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium hidden sm:table-cell" style={{ color: 'var(--dash-text-dim)' }}>
                  Date
                </th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {apps.map((app, i) => (
                <tr key={app.id}>
                  <td colSpan={5} className="p-0">
                    <div
                      style={{
                        background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                        borderBottom: '1px solid var(--dash-border-light)',
                      }}
                    >
                      <div className="flex items-center">
                        <div className="flex-1 grid grid-cols-[1fr] md:grid-cols-[1fr_1fr] sm:grid-cols-[1fr_auto_auto] lg:grid-cols-[1fr_1fr_auto_auto_auto] items-center">
                          <div className="px-4 py-3">
                            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-secondary)' }}>{app.business_name}</p>
                            <a
                              href={app.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-[10px] transition-colors"
                              style={{ color: 'var(--dash-text-dim)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-dim)'; }}
                            >
                              {app.website}
                            </a>
                          </div>
                          <div className="px-4 py-3 hidden md:block">
                            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>{app.full_name}</p>
                            <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>{app.email}</p>
                          </div>
                          <div className="px-4 py-3">
                            <span
                              className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${statusColor(app.status)}`}
                            >
                              {app.status}
                            </span>
                          </div>
                          <div className="px-4 py-3 hidden sm:block">
                            <p className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                              {new Date(app.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="px-4 py-3 flex items-center gap-2 justify-end">
                            {app.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => updateStatus(app.id, 'approved')}
                                  disabled={updatingId === app.id}
                                  className="w-7 h-7 rounded bg-green-500/10 border border-green-500/20 flex items-center justify-center hover:bg-green-500/20 transition-colors disabled:opacity-50"
                                  title="Approve"
                                >
                                  {updatingId === app.id ? (
                                    <Loader2 size={14} className="text-green-400 animate-spin" />
                                  ) : (
                                    <Check size={14} className="text-green-400" />
                                  )}
                                </button>
                                <button
                                  onClick={() => updateStatus(app.id, 'rejected')}
                                  disabled={updatingId === app.id}
                                  className="w-7 h-7 rounded bg-red-500/10 border border-red-500/20 flex items-center justify-center hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                  title="Reject"
                                >
                                  <X size={14} className="text-red-400" />
                                </button>
                              </>
                            )}
                            {app.status === 'approved' && (
                              <button
                                onClick={() => resendOnboardingEmail(app.id)}
                                disabled={resendingId === app.id}
                                className="h-7 px-2.5 rounded bg-sky-500/10 border border-sky-500/20 flex items-center gap-1.5 hover:bg-sky-500/20 transition-colors disabled:opacity-50"
                                title="Resend onboarding email"
                              >
                                {resendingId === app.id ? (
                                  <Loader2 size={13} className="text-sky-400 animate-spin" />
                                ) : (
                                  <Mail size={13} className="text-sky-400" />
                                )}
                                <span className="font-mono text-[10px] text-sky-400 uppercase tracking-widest">
                                  Resend
                                </span>
                              </button>
                            )}
                            <button
                              onClick={() =>
                                setExpandedId(expandedId === app.id ? null : app.id)
                              }
                              className="w-7 h-7 rounded flex items-center justify-center transition-colors"
                              title="Details"
                              style={{
                                background: 'var(--dash-hover-bg)',
                                border: '1px solid var(--dash-border)',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                            >
                              {expandedId === app.id ? (
                                <ChevronUp size={14} style={{ color: 'var(--dash-text-muted)' }} />
                              ) : (
                                <ChevronDown size={14} style={{ color: 'var(--dash-text-muted)' }} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      {expandedId === app.id && (
                        <div
                          className="px-4 pb-4 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-4"
                          style={{ borderTop: '1px solid var(--dash-border-light)' }}
                        >
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                              Full Name
                            </p>
                            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>{app.full_name}</p>
                          </div>
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                              Email
                            </p>
                            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>{app.email}</p>
                          </div>
                          {app.phone && (
                            <div>
                              <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                                Phone
                              </p>
                              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>{app.phone}</p>
                            </div>
                          )}
                          {app.notes && (
                            <div className="sm:col-span-2">
                              <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                                Notes
                              </p>
                              <p className="font-sans text-sm whitespace-pre-wrap" style={{ color: 'var(--dash-text-tertiary)' }}>
                                {app.notes}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                              Submitted
                            </p>
                            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>
                              {new Date(app.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
